import { MEDIA_QUERIES } from '@/constants';
import { useMediaQuery } from '@/hooks/use-media-query';
import { setAnalyticsConsent, shouldShowConsentDialog } from '@/utils/analytics';
import React, { useEffect, useState } from 'react';

interface AnalyticsConsentProps {
    onConsentChange?: (hasConsent: boolean) => void;
}

export const AnalyticsConsent: React.FC<AnalyticsConsentProps> = ({ onConsentChange }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [timeLeft, setTimeLeft] = useState(10);
    const isMobile = !useMediaQuery(MEDIA_QUERIES.TABLET_OR_LARGER);

    useEffect(() => {
        // Check if consent dialog should be shown
        if (shouldShowConsentDialog()) {
            // Show consent popup after a short delay
            const timer = setTimeout(() => {
                setIsVisible(true);
                setIsAnimating(true);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, []);

    // Auto-consent timer
    useEffect(() => {
        if (!isVisible || !isAnimating) return;

        const interval = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    // Auto-consent after 10 seconds
                    handleAccept();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isVisible, isAnimating]);

    const handleAccept = () => {
        setAnalyticsConsent(true);
        onConsentChange?.(true);
        closePopup();
    };

    const handleDecline = () => {
        setAnalyticsConsent(false);
        onConsentChange?.(false);
        closePopup();
    };

    const closePopup = () => {
        setIsAnimating(false);
        setTimeLeft(10); // Reset timer
        setTimeout(() => {
            setIsVisible(false);
        }, 300);
    };

    if (!isVisible) {
        return null;
    }

    return (
        <>
            {/* Mobile backdrop */}
            {isMobile && (
                <div
                    className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
                        isAnimating ? 'opacity-100' : 'opacity-0'
                    }`}
                    onClick={closePopup}
                />
            )}

            <div
                className={`fixed z-50 transition-all duration-300 ease-in-out ${
                    isMobile ? 'bottom-0 left-0 right-0 px-4 pt-4 pb-4' : 'bottom-8 right-2'
                } ${
                    isAnimating
                        ? isMobile
                            ? 'translate-y-0 opacity-100'
                            : 'translate-y-0 opacity-100'
                        : isMobile
                          ? 'translate-y-full opacity-0'
                          : 'translate-y-full opacity-0'
                }`}
            >
                <div
                    className={`bg-vscode-bg border border-vscode-border rounded-lg shadow-2xl p-4 ${
                        isMobile ? 'w-full max-w-none rounded-t-lg' : 'max-w-sm'
                    }`}
                >
                    {/* Header */}
                    <div className='flex items-center justify-between mb-3'>
                        <div className='flex items-center space-x-2'>
                            <span className={`text-vscode-text font-bold ${isMobile ? 'text-sm' : 'text-md'}`}>
                                Analytics
                            </span>
                        </div>
                        <button
                            onClick={closePopup}
                            className='text-vscode-text-muted hover:text-vscode-text transition-colors'
                            aria-label='Close'
                        >
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M6 18L18 6M6 6l12 12'
                                />
                            </svg>
                        </button>
                    </div>
                    <hr className='my-2' />

                    {/* Content */}
                    <div className='mb-4'>
                        <p className={`text-vscode-text leading-relaxed ${isMobile ? 'text-base' : 'text-sm'}`}>
                            This site uses Google Analytics to understand how visitors interact with the site.
                        </p>
                        <p className={`text-vscode-text-muted mt-2 ${isMobile ? 'text-sm' : 'text-xs'}`}>
                            No personal data is collected. You can decline and continue using the site normally.
                        </p>
                    </div>

                    {/* Actions */}
                    <div className={`flex space-x-2 ${isMobile ? 'space-x-3' : ''}`}>
                        <button
                            onClick={handleAccept}
                            className={`flex-1 bg-green-600 hover:bg-green-700 hover:shadow-lg border border-green-500 text-white font-medium rounded transition-all duration-200 cursor-pointer active:scale-95 hover:scale-105 ${
                                isMobile ? 'px-3 py-2 text-sm' : 'px-3 py-2 text-sm'
                            }`}
                        >
                            ✓ Accept {timeLeft > 0 && `(${timeLeft}s)`}
                        </button>
                        <button
                            onClick={handleDecline}
                            className={`flex-1 bg-red-900/20 border border-red-600 hover:bg-red-900/30 hover:border-red-500 hover:shadow-lg text-red-400 font-medium rounded transition-all duration-200 cursor-pointer active:scale-95 hover:scale-105 ${
                                isMobile ? 'px-3 py-2 text-sm' : 'px-3 py-2 text-sm'
                            }`}
                        >
                            ❌ Decline
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
